import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import type {
  ApcmOpsTab,
  ApcmPatientRow,
  LifecycleSummary,
  PatientAPCMStateHistory,
  PatientCurrentState,
  PendingCarePlan,
  PreflightCheck,
  WorkflowStatus,
  APCMState,
} from "../types/apcm-ops.types"
import * as api from "../api/apcm-ops.api"
import type { APCMStatsResult } from "../api/apcm-ops.api"
import { describeActionResult } from "../lib/program"

interface ApcmOpsState {
  // Data
  lifecycleSummary: LifecycleSummary | null
  allPatientStates: PatientCurrentState[]
  pendingCarePlans: PendingCarePlan[]
  stats: APCMStatsResult | null

  // Table state
  stateFilter: string | null
  searchQuery: string
  selectedPatientIds: Record<string, true>

  // Drawer
  drawerPatientId: string | null
  patientHistory: PatientAPCMStateHistory | null
  activeWorkflow: WorkflowStatus | null

  // Deployment
  preflightCheck: PreflightCheck | null
  preflightLoading: boolean

  // UI
  activeTab: ApcmOpsTab
  isLoading: boolean
  actionInProgress: string | null
  lastActionResult: { success: boolean; message: string } | null
  error: string | null
}

interface ApcmOpsActions {
  // Data fetching
  fetchAll(orgId: string): Promise<void>
  fetchLifecycleSummary(orgId: string): Promise<void>
  fetchAllStates(orgId: string): Promise<void>
  fetchPendingCarePlans(orgId: string): Promise<void>
  fetchStats(orgId: string): Promise<void>
  fetchPreflightCheck(orgId: string): Promise<void>

  // Drawer
  openDrawer(patientId: string, orgId: string): Promise<void>
  closeDrawer(): void

  // Filters
  setStateFilter(state: string | null): void
  setSearchQuery(query: string): void
  setActiveTab(tab: ApcmOpsTab): void

  // Selection
  togglePatientSelection(patientId: string): void
  selectAllVisible(ids: string[]): void
  clearSelection(): void

  // Actions
  executeAction(name: string, fn: () => Promise<any>): Promise<void>
  clearActionResult(): void
}

export const useApcmOpsStore = create<ApcmOpsState & ApcmOpsActions>()(
  immer((set, get) => ({
    // Initial state
    lifecycleSummary: null,
    allPatientStates: [],
    pendingCarePlans: [],
    stats: null,
    stateFilter: null,
    searchQuery: "",
    selectedPatientIds: {},
    drawerPatientId: null,
    patientHistory: null,
    activeWorkflow: null,
    preflightCheck: null,
    preflightLoading: false,
    activeTab: "pipeline",
    isLoading: false,
    actionInProgress: null,
    lastActionResult: null,
    error: null,

    // Data fetching
    fetchAll: async (orgId) => {
      set((s) => { s.isLoading = true; s.error = null })
      // Each call is independent: one failing endpoint (all-states 500'd on a
      // null metadata row on 2026-09-08) must not blank the whole page.
      const [lifecycle, states, pending, statsResult] = await Promise.allSettled([
        api.getLifecycleSummary(orgId),
        api.getAllStates(orgId),
        api.getPendingCarePlans(orgId),
        api.getAPCMStats(orgId),
      ])
      const failed = [lifecycle, states, pending].filter((r) => r.status === "rejected") as PromiseRejectedResult[]
      set((s) => {
        if (lifecycle.status === "fulfilled") s.lifecycleSummary = lifecycle.value
        if (states.status === "fulfilled") s.allPatientStates = states.value
        if (pending.status === "fulfilled") s.pendingCarePlans = pending.value
        if (statsResult.status === "fulfilled") s.stats = statsResult.value
        s.error = failed.length
          ? failed.map((r) => r.reason?.message || "Failed to fetch APCM data").join("; ")
          : null
        s.isLoading = false
      })
    },

    fetchLifecycleSummary: async (orgId) => {
      const data = await api.getLifecycleSummary(orgId)
      set((s) => { s.lifecycleSummary = data })
    },

    fetchAllStates: async (orgId) => {
      const data = await api.getAllStates(orgId)
      set((s) => { s.allPatientStates = data })
    },

    fetchPendingCarePlans: async (orgId) => {
      const data = await api.getPendingCarePlans(orgId)
      set((s) => { s.pendingCarePlans = data })
    },

    fetchStats: async (orgId) => {
      try {
        const data = await api.getAPCMStats(orgId)
        set((s) => { s.stats = data })
      } catch { /* ignore */ }
    },

    fetchPreflightCheck: async (orgId) => {
      set((s) => { s.preflightLoading = true })
      try {
        const data = await api.getPreflightCheck(orgId)
        set((s) => { s.preflightCheck = data; s.preflightLoading = false })
      } catch (err: any) {
        set((s) => {
          s.preflightLoading = false
          s.error = err?.message || "Failed to fetch preflight check"
        })
      }
    },

    // Drawer
    openDrawer: async (patientId, orgId) => {
      set((s) => {
        s.drawerPatientId = patientId
        s.patientHistory = null
        s.activeWorkflow = null
      })
      try {
        const [history, workflow] = await Promise.allSettled([
          api.getPatientHistory(patientId, orgId),
          api.getActiveWorkflow(patientId, orgId),
        ])
        set((s) => {
          if (history.status === "fulfilled") s.patientHistory = history.value
          if (workflow.status === "fulfilled") s.activeWorkflow = workflow.value
        })
      } catch { /* ignore partial failures */ }
    },

    closeDrawer: () => {
      set((s) => {
        s.drawerPatientId = null
        s.patientHistory = null
        s.activeWorkflow = null
      })
    },

    // Filters
    setStateFilter: (state) => set((s) => { s.stateFilter = state }),
    setSearchQuery: (query) => set((s) => { s.searchQuery = query }),
    setActiveTab: (tab) => set((s) => { s.activeTab = tab }),

    // Selection
    togglePatientSelection: (patientId) => {
      set((s) => {
        if (s.selectedPatientIds[patientId]) {
          delete s.selectedPatientIds[patientId]
        } else {
          s.selectedPatientIds[patientId] = true
        }
      })
    },
    selectAllVisible: (ids) => {
      set((s) => {
        s.selectedPatientIds = Object.fromEntries(ids.map((id) => [id, true as const]))
      })
    },
    clearSelection: () => {
      set((s) => { s.selectedPatientIds = {} })
    },

    // Actions
    executeAction: async (name, fn) => {
      set((s) => { s.actionInProgress = name; s.lastActionResult = null })
      try {
        const result = await fn()
        set((s) => {
          s.actionInProgress = null
          s.lastActionResult = describeActionResult(name, result)
        })
      } catch (err: any) {
        set((s) => {
          s.actionInProgress = null
          s.lastActionResult = {
            success: false,
            message: err?.response?.data?.detail || err?.message || `${name} failed`,
          }
        })
      }
    },

    clearActionResult: () => set((s) => { s.lastActionResult = null }),
  }))
)
